import React, { FunctionComponent, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { generatePath, useNavigate } from "react-router-dom";

import {
  FontSizes,
  LabeledText,
  LabeledTextGroup,
  LabeledTextStyles,
  Link,
  Shimmer,
  Stack,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  useTheme
} from "@bps/fluent-ui";
import { ErrorAlert } from "@components/errors/ErrorAlert";
import { IdentityIcon } from "@components/IdentityIcon";
import { SecurableComponent } from "@components/SecurableComponent";
import { useTabsNavContext } from "@components/tabs-nav";
import {
  ApplicationType,
  Tenant
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useChildTenants,
  useTenantQuery
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { PltTenantApplicationBadge } from "../../../../../_shared-blocks/PltTenantApplicationBadge";
import { TenantsTabs } from "../../../../tenants-tabs.constants";
import { ManageTenantDialog } from "../../components/components/manage-tenant-dialog/ManageTenantDialog";
import { TransferTenantDialog } from "../../components/components/transfer-tenant-dialog/TransferTenantDialog";
import { AssignTenantForm } from "./AssignTenantForm";
import { SelfAssertedCrmDetails } from "./SelfAssertedCrmDetails";
import {
  ItemsKeys,
  LineItem,
  TenantDetailsTableProps
} from "./TenantDetailsTable.types";
import { TenantUnlinkButtons } from "./TenantUnlinkButtons";

const labeledTextStyles: Partial<LabeledTextStyles> = {
  root: {
    gridTemplateColumns: "0.2fr 1fr",
    minHeight: 32,
    columnGap: "8px",
    alignItems: "center"
  },
  label: { fontSize: FontSizes.medium }
};
export const TenantDetailsTable: FunctionComponent<TenantDetailsTableProps> = ({
  tenant
}) => {
  const isCamTenant = tenant.application === ApplicationType.CAM;
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { addTab } = useTabsNavContext();
  const navigate = useNavigate();
  const {
    data: childTenants = [],
    error: errorChildTenants,
    isLoading: isLoadingChildTenants,
    refetch
  } = useChildTenants(tenant.id, { enabled: !tenant.customerTenantId });

  const [manageTenantTenant, manageTenant] = useState<Tenant>();
  const [showTransferTenant, transferTenant] = useState<boolean>(false);
  const [
    transferSourceTenant,
    setTransferSourceTenant
  ] = useState<Tenant | null>(null);

  const [
    transferTargetTenant,
    setTransferTargetTenant
  ] = useState<Tenant | null>(null);

  const selectFieldmanDevice = (id?: string) => {
    const to = generatePath("/field-mgmt/:deviceId/fd-overview", {
      deviceId: id
    });
    navigate(to);
  };

  const {
    data: customerTenant,
    error: errorCustomerTenants,
    isLoading: isLoadingCustomerTenants
  } = useTenantQuery(tenant.customerTenantId!, {
    enabled: !!tenant.customerTenantId
  });

  const setSelectedTab = (childTenantId: string, name: string) => {
    addTab({
      id: childTenantId,
      name,
      data: { sectionId: TenantsTabs.Platform.Details.id }
    });
  };

  const selectCustomer = (crmId?: string) => {
    const to = generatePath("/cam/:crmId/ca-crmclientdetails", {
      crmId
    });
    navigate(to);
  };

  const renderRow = (item: LineItem) => {
    const { section, value } = item;

    if (
      section === ItemsKeys.SelfAssertedCrmId &&
      tenant.application === ApplicationType.PROS
    ) {
      return <SelfAssertedCrmDetails tenant={tenant} />;
    }

    if (section === ItemsKeys.Type) {
      return <PltTenantApplicationBadge applicationCode={value} />;
    }

    if (section === ItemsKeys.CrmId) {
      return (
        <Link key={item.name} onClick={() => selectCustomer(item.value)}>
          {item.value}
        </Link>
      );
    }

    if (section === ItemsKeys.Fieldman) {
      return (
        <Stack horizontal tokens={{ childrenGap: theme.spacing.m }}>
          <TextBadge
            badgeSize={TextBadgeSize.small}
            badgeColor={!value ? TextBadgeColor.red : TextBadgeColor.green}
            styles={{ root: { width: "fit-content" } }}
          >
            {value ? "Yes" : "No"}
          </TextBadge>
          {value && (
            <Stack horizontal tokens={{ childrenGap: theme.spacing.s1 }}>
              <IdentityIcon size={22} id={value} />
              <Link key={value} onClick={() => selectFieldmanDevice(value)}>
                {value}
              </Link>
            </Stack>
          )}
        </Stack>
      );
    }

    if (section === ItemsKeys.RelatedTenants) {
      const inTransferMode = !!transferSourceTenant;
      return (
        <Stack tokens={{ childrenGap: 8 }}>
          {manageTenantTenant && (
            <ManageTenantDialog
              tenant={manageTenantTenant}
              onDismiss={() => manageTenant(undefined)}
              onUnlinked={() => {
                manageTenant(undefined);
                refetch();
              }}
            />
          )}

          {showTransferTenant && transferSourceTenant && transferTargetTenant && (
            <TransferTenantDialog
              onDismiss={() => transferTenant(false)}
              onTransferred={async () => {
                setTransferTargetTenant(null);
                setTransferSourceTenant(null);
                transferTenant(false);
                refetch();
                await queryClient.invalidateQueries(["canUnlinkTenant"]);
              }}
              source={transferSourceTenant}
              target={transferTargetTenant}
            />
          )}
          {!tenant.customerTenantId && (
            <AssignTenantForm
              tenantId={tenant.id}
              country={tenant.country}
              assignChildTenant={isCamTenant}
              excludeKeys={childTenants.map(tenant => tenant.id)}
            />
          )}
          <Stack
            tokens={{ childrenGap: 8 }}
            styles={{
              root: {
                backgroundColor: inTransferMode
                  ? theme.semanticColors.buttonBackgroundDisabled
                  : "transparent",
                padding: inTransferMode ? "16px" : undefined,
                marginRight: "auto"
              }
            }}
          >
            {value?.map(item => {
              const tenantLink = (
                <Link
                  key={item.id}
                  onClick={() => setSelectedTab(item.id, item.name)}
                >
                  {item.name} {item.isInactive && "(Inactive)"}
                </Link>
              );

              const canTransfer = value.length > 1;
              return isCamTenant ? (
                <Stack tokens={{ childrenGap: 8 }} horizontal>
                  {tenantLink}
                  <SecurableComponent
                    showError={false}
                    permissions={[Permissions.PltCatalogTenantUnlink]}
                  >
                    <TenantUnlinkButtons
                      tenant={item}
                      onTransferClick={clickedTenant =>
                        setTransferSourceTenant(clickedTenant)
                      }
                      onUnlinkClick={clickedTenant =>
                        manageTenant(clickedTenant)
                      }
                      canTransfer={canTransfer}
                      isTransferMode={!!transferSourceTenant}
                      isSource={transferSourceTenant?.id === item.id}
                      isTarget={
                        !!transferSourceTenant &&
                        transferSourceTenant.id !== item.id
                      }
                      onSelectTarget={() => {
                        setTransferTargetTenant(item);
                        transferTenant(true);
                      }}
                      onCancelTransfer={() => {
                        setTransferSourceTenant(null);
                      }}
                    />
                  </SecurableComponent>
                </Stack>
              ) : (
                tenantLink
              );
            })}
          </Stack>
        </Stack>
      );
    }

    if (!Array.isArray(value)) return <>{value}</>;
    if (!value.length) return <>--</>;

    return (
      <Stack
        tokens={{ maxHeight: 400 }}
        styles={{ root: { overflowY: "auto" } }}
      >
        {value.map(item => (
          <div key={item}>{item}</div>
        ))}
      </Stack>
    );
  };

  const items = useMemo((): LineItem[] => {
    const getRelatedTenantValue = () => {
      if (isCamTenant) return childTenants;
      return customerTenant ? [customerTenant] : [];
    };

    return [
      { section: ItemsKeys.Name, value: tenant.name, name: "Name" },
      { section: ItemsKeys.CrmId, value: tenant.crmId, name: "Crm Client Id" },
      {
        section: ItemsKeys.SelfAssertedCrmId,
        value: tenant.selfAssertedCrmId,
        name: "Self Asserted Crm Client Id"
      },
      {
        section: ItemsKeys.Type,
        value: tenant.application,
        name: "Application"
      },
      {
        section: ItemsKeys.Country,
        value: tenant.country,
        name: "Country"
      },
      {
        section: ItemsKeys.RelatedTenants,
        value: getRelatedTenantValue(),
        name: isCamTenant ? "Child tenants" : "Customer tenant"
      },
      {
        section: ItemsKeys.UserLimit,
        value: tenant.userLimit,
        name: "Users limit"
      },
      {
        section: ItemsKeys.Permissions,
        value: tenant.permissions,
        name: "Permissions"
      },
      {
        section: ItemsKeys.Fieldman,
        value: tenant.siteAuthorityFieldDeviceId,
        name: "Created by Fieldman"
      }
    ];
  }, [
    childTenants,
    customerTenant,
    isCamTenant,
    tenant.application,
    tenant.country,
    tenant.crmId,
    tenant.name,
    tenant.permissions,
    tenant.selfAssertedCrmId,
    tenant.siteAuthorityFieldDeviceId,
    tenant.userLimit
  ]);

  if (errorChildTenants ?? errorCustomerTenants)
    return <ErrorAlert error={errorChildTenants ?? errorCustomerTenants} />;

  return (
    <LabeledTextGroup>
      {items.map(item => (
        <LabeledText
          key={item.section}
          label={item.name}
          wrap
          styles={labeledTextStyles}
        >
          {isLoadingChildTenants || isLoadingCustomerTenants ? (
            <Shimmer styles={{ shimmerWrapper: { height: 16 } }} width={100} />
          ) : (
            renderRow(item)
          )}
        </LabeledText>
      ))}
    </LabeledTextGroup>
  );
};
